// src/components/admin/AssignmentEvaluationModal.js
// Admin-only: view a learner's 6 assignments, open a submission, and score
// it on 4 domains (1-5 each). Writes to the `evaluations` collection, which
// Firestore rules restrict to admin read/write only.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  doc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';

const DOMAINS = [
  { key: 'originality', label: 'Originality' },
  { key: 'practicality', label: 'Practicality' },
  { key: 'understanding', label: 'Understanding' },
  { key: 'presentation', label: 'Presentation' },
];

const DEFAULT_SCORES = { originality: 3, practicality: 3, understanding: 3, presentation: 3 };

function AssignmentEvaluationModal({ learner, onClose }) {
  const { currentUser } = useAuth();

  const [assignments, setAssignments] = useState([]);
  const [submissionsByAssignment, setSubmissionsByAssignment] = useState({});
  const [evaluationsByAssignment, setEvaluationsByAssignment] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [openAssignmentId, setOpenAssignmentId] = useState(null);
  const [scores, setScores] = useState(DEFAULT_SCORES);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [assignmentsSnap, submissionsSnap, evaluationsSnap] = await Promise.all([
        getDocs(query(collection(db, 'assignments'), orderBy('order'))),
        getDocs(query(collection(db, 'submissions'), where('learnerId', '==', learner.id))),
        getDocs(query(collection(db, 'evaluations'), where('learnerId', '==', learner.id))),
      ]);

      setAssignments(assignmentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const subMap = {};
      submissionsSnap.docs.forEach(d => {
        subMap[d.data().assignmentId] = { id: d.id, ...d.data() };
      });
      setSubmissionsByAssignment(subMap);

      const evalMap = {};
      evaluationsSnap.docs.forEach(d => {
        evalMap[d.data().assignmentId] = { id: d.id, ...d.data() };
      });
      setEvaluationsByAssignment(evalMap);
    } catch (err) {
      setError('Failed to load evaluation data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [learner.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const openAssignment = (assignmentId) => {
    setOpenAssignmentId(assignmentId);
    const existing = evaluationsByAssignment[assignmentId];
    setScores(existing?.scores || DEFAULT_SCORES);
  };

  const average = useMemo(() => {
    const vals = DOMAINS.map(d => scores[d.key] || 0);
    return (vals.reduce((a, b) => a + b, 0) / DOMAINS.length).toFixed(2);
  }, [scores]);

  const saveEvaluation = async () => {
    if (!openAssignmentId) return;
    try {
      setSaving(true);
      setError(null);
      // Deterministic id (learnerId_assignmentId) — one evaluation per
      // learner+assignment, no separate existence check needed, `merge`
      // handles both create and update.
      const evalId = `${learner.id}_${openAssignmentId}`;
      const existing = evaluationsByAssignment[openAssignmentId];

      await setDoc(doc(db, 'evaluations', evalId), {
        learnerId: learner.id,
        assignmentId: openAssignmentId,
        groupId: learner.groupId || null,
        evaluatorId: currentUser?.uid || null,
        scores,
        averageScore: Number(average),
        createdAt: existing?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setEvaluationsByAssignment(prev => ({
        ...prev,
        [openAssignmentId]: { ...(prev[openAssignmentId] || {}), scores, averageScore: Number(average) },
      }));
      setOpenAssignmentId(null);
    } catch (err) {
      setError('Failed to save evaluation: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openAssignmentDetail = openAssignmentId
    ? assignments.find(a => a.id === openAssignmentId)
    : null;
  const openSubmission = openAssignmentId ? submissionsByAssignment[openAssignmentId] : null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: '10px', padding: '20px 24px',
          width: '90%', maxWidth: '640px', maxHeight: '85vh', overflowY: 'auto',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ margin: 0 }}>Evaluate: {learner.name}</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#666' }}>
            ✕
          </button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '10px' }}>{error}</div>}

        {loading ? (
          <p>Loading assignments…</p>
        ) : openAssignmentId ? (
          // ── Submission + scoring view ─────────────────────────────────────
          <div>
            <button
              onClick={() => setOpenAssignmentId(null)}
              style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, marginBottom: '12px' }}>
              ← Back to assignments
            </button>
            <h4 style={{ marginBottom: '4px' }}>{openAssignmentDetail?.title || 'Untitled assignment'}</h4>

            {!openSubmission ? (
              <p style={{ color: '#9ca3af' }}>No submission yet.</p>
            ) : (
              <div style={{ marginBottom: '16px' }}>
                {openSubmission.content && (
                  <p style={{ whiteSpace: 'pre-wrap', background: '#f9fafb', padding: '10px', borderRadius: '6px' }}>
                    {openSubmission.content}
                  </p>
                )}
                {(openSubmission.fileUrls || []).map((f, i) => (
                  <div key={i} style={{ marginBottom: '10px' }}>
                    <a href={f.url} target="_blank" rel="noreferrer">{f.name || `File ${i + 1}`}</a>
                    {f.type?.includes('pdf') && (
                      <iframe
                        src={f.url}
                        title={f.name || `submission-file-${i}`}
                        style={{ width: '100%', height: '380px', border: '1px solid #e5e7eb', borderRadius: '4px', marginTop: '6px' }}
                      />
                    )}
                    {f.type?.startsWith('image') && (
                      <img src={f.url} alt={f.name || ''} style={{ maxWidth: '100%', borderRadius: '4px', marginTop: '6px' }} />
                    )}
                  </div>
                ))}
                {(!openSubmission.fileUrls || openSubmission.fileUrls.length === 0) && !openSubmission.content && (
                  <p style={{ color: '#9ca3af' }}>Submission has no content or files.</p>
                )}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              {DOMAINS.map(d => (
                <div key={d.key}>
                  <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>{d.label}</label>
                  <select
                    value={scores[d.key]}
                    onChange={e => setScores(prev => ({ ...prev, [d.key]: Number(e.target.value) }))}
                    style={{ width: '100%', padding: '4px' }}>
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <p style={{ fontWeight: 600 }}>Average: {average}</p>

            <button className="btn-primary" disabled={saving} onClick={saveEvaluation}>
              {saving ? 'Saving…' : 'Save evaluation'}
            </button>
          </div>
        ) : (
          // ── 6-assignment list view ──────────────────────────────────────────
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {assignments.length === 0 && <p style={{ color: '#9ca3af' }}>No assignments found.</p>}
            {assignments.map(a => {
              const sub = submissionsByAssignment[a.id];
              const evaluation = evaluationsByAssignment[a.id];
              return (
                <li
                  key={a.id}
                  onClick={() => openAssignment(a.id)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 4px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                  }}>
                  <span>{a.title || 'Untitled assignment'}</span>
                  <span style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <span style={{
                      fontSize: '0.75rem', padding: '2px 8px', borderRadius: '999px',
                      background: sub ? '#dcfce7' : '#f3f4f6', color: sub ? '#166534' : '#9ca3af',
                    }}>
                      {sub ? 'Submitted' : 'Not submitted'}
                    </span>
                    <span style={{
                      fontSize: '0.75rem', padding: '2px 8px', borderRadius: '999px',
                      background: evaluation ? '#e6f1fb' : '#f3f4f6', color: evaluation ? '#0c4a6e' : '#9ca3af',
                    }}>
                      {evaluation ? `Scored ${evaluation.averageScore}` : 'Unscored'}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default AssignmentEvaluationModal;